<template>
  <div :class="loadingClasses">
    <div class="bert-loading-spinner" :class="spinnerClasses">
      <slot name="spinner">
        <svg class="bert-loading-svg" viewBox="0 0 50 50">
          <circle
            class="bert-loading-circle"
            cx="25"
            cy="25"
            r="20"
            fill="none"
            stroke="currentColor"
            stroke-width="4"
          />
        </svg>
      </slot>
    </div>
    <div v-if="text || $slots.default" class="bert-loading-text">
      <slot>{{ text }}</slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BertLoading',
  props: {
    text: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl'].includes(value)
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'neutral'].includes(value)
    },
    overlay: {
      type: Boolean,
      default: false
    },
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    loadingClasses() {
      return [
        'bert-loading',
        {
          'bert-loading--overlay': this.overlay,
          'bert-loading--fullscreen': this.fullscreen
        }
      ]
    },
    spinnerClasses() {
      return [
        'bert-loading-spinner',
        `bert-loading-spinner--${this.size}`,
        `bert-loading-spinner--${this.variant}`
      ]
    }
  }
}
</script>

<style scoped>
.bert-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
}

.bert-loading--overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: var(--z-modal);
}

.bert-loading--fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: var(--z-modal);
}

.bert-loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Sizes */
.bert-loading-spinner--sm {
  width: 24px;
  height: 24px;
}

.bert-loading-spinner--md {
  width: 32px;
  height: 32px;
}

.bert-loading-spinner--lg {
  width: 48px;
  height: 48px;
}

.bert-loading-spinner--xl {
  width: 64px;
  height: 64px;
}

/* Variants */
.bert-loading-spinner--primary {
  color: var(--primary-600);
}

.bert-loading-spinner--secondary {
  color: var(--secondary-600);
}

.bert-loading-spinner--neutral {
  color: var(--neutral-600);
}

.bert-loading-svg {
  width: 100%;
  height: 100%;
  animation: rotate 2s linear infinite;
}

.bert-loading-circle {
  stroke-linecap: round;
  stroke-dasharray: 90, 150;
  stroke-dashoffset: 0;
  animation: dash 1.5s ease-in-out infinite;
}

.bert-loading-text {
  font-size: var(--text-sm);
  color: var(--neutral-600);
  text-align: center;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}
</style>